import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Alert, Box, Breadcrumbs, Button, Link, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import * as invoicesApi from '@/api/invoices';
import { toApiError } from '@/api/client';
import { LoadingScreen } from '@/components/LoadingScreen';
import { InvoiceDetail } from '@/components/InvoiceDetail';
import { InvoiceActionsMenu } from '@/components/InvoiceActionsMenu';
import { InvoiceStatusChip } from '@/components/InvoiceStatusChip';
import { INVOICE_KIND_LABEL } from '@/lib/labels';
import { formatInvoiceNumber } from '@/lib/formatters';
import { useCurrentCuit } from '@/auth/CuitContext';

export const Route = createFileRoute('/_authenticated/invoices/$id')({
  component: InvoiceDetailPage,
});

function InvoiceDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCuitProfileId } = useCurrentCuit();

  const invoiceQ = useQuery({
    queryKey: ['invoice', id, currentCuitProfileId],
    queryFn: () => invoicesApi.getInvoice(id, currentCuitProfileId),
    refetchInterval: 30_000,
  });

  useEffect(() => {
    if (invoiceQ.data?.status === 'Draft') {
      void navigate({ to: '/invoices/$id/edit', params: { id }, replace: true });
    }
  }, [id, invoiceQ.data?.status, navigate]);

  const retryMutation = useMutation({
    mutationFn: () => invoicesApi.retryInvoice(id, currentCuitProfileId),
    onSuccess: (invoice) => {
      queryClient.setQueryData(['invoice', id, currentCuitProfileId], invoice);
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  if (invoiceQ.isLoading) return <LoadingScreen />;

  if (invoiceQ.isError || !invoiceQ.data) {
    return (
      <Box sx={{ maxWidth: 720, mx: 'auto' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate({ to: '/invoices' })} sx={{ mb: 2 }}>
          Volver
        </Button>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => invoiceQ.refetch()}>
              Reintentar
            </Button>
          }
        >
          {invoiceQ.error ? toApiError(invoiceQ.error).message : 'No se pudo cargar la factura.'}
        </Alert>
      </Box>
    );
  }

  const invoice = invoiceQ.data;
  if (invoice.status === 'Draft') {
    return <LoadingScreen message="Abriendo el editor del borrador…" />;
  }
  const fullNumber = formatInvoiceNumber(invoice.pointOfSale, invoice.number);

  return (
    <Box>
      <Button
        startIcon={<ArrowBack />}
        onClick={() => navigate({ to: '/invoices' })}
        sx={{ mb: 2 }}
      >
        Volver
      </Button>

      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          color="inherit"
          underline="hover"
          onClick={() => navigate({ to: '/invoices' })}
        >
          Comprobantes
        </Link>
        <Typography color="text.primary" sx={{ fontFamily: 'var(--font-mono)' }}>
          #{fullNumber}
        </Typography>
      </Breadcrumbs>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'flex-start' }}
        sx={{ mb: 3, gap: 2 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontFamily: 'var(--font-mono)' }}>
            {fullNumber}
          </Typography>
          <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
            <InvoiceStatusChip value={invoice.status} isVoided={invoice.isVoided} />
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              {INVOICE_KIND_LABEL[invoice.kind]}
            </Typography>
          </Stack>
        </Box>
        <InvoiceActionsMenu invoice={invoice} />
      </Stack>

      {retryMutation.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {toApiError(retryMutation.error).message}
        </Alert>
      ) : null}

      <InvoiceDetail
        invoice={invoice}
        onRetry={invoice.status === 'Pending' ? () => retryMutation.mutate() : undefined}
        retrying={retryMutation.isPending}
      />
    </Box>
  );
}
